import {
  createLegalAIAssistantClient,
  ContractAnalysisResponse,
} from "../../api/client";
import {
  BodyAnalyzeContractContractReviewAnalyzePost,
  BodyUploadAndAnalyzeContractContractReviewUploadAnalyzePost,
} from "../../apiClient/data-contracts";
import {
  API_BASE_URL,
  DEFAULT_LANGUAGE,
  DEFAULT_JURISDICTION,
  SUPABASE_S3_ACCESS_KEY_ID,
  SUPABASE_S3_SECRET_ACCESS_KEY,
  SUPABASE_S3_BUCKET,
  SUPABASE_S3_REGION,
  SUPABASE_S3_ENDPOINT,
  // Cloudflare R2 Configuration (COMMENTED OUT - USING SUPABASE NOW)
  // R2_BUCKET,
  // R2_ENDPOINT,
  // CLOUDFLARE_API_TOKEN,
  // CLOUDFLARE_ACCOUNT_ID,
  // UPLOAD_WORKER_URL,
} from "../../config";
import React, { useState, useCallback, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { getAgentBySlug, Agent } from "@/lib/agents";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import {
  UploadCloud,
  FileText,
  CheckCircle,
  ShieldAlert,
  AlertCircle,
  Download,
  Share2,
  Loader2,
  RefreshCw,
  AlertTriangle,
  XCircle,
  Info,
  Search,
  History,
  Calendar,
  Clock,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Gavel, FolderOpen, FileCheck } from "lucide-react";
import { fetchScrapperProcesos, getProcesoFiles, ScrapperProceso, ProcesoArchivo } from "@/services/scraperApi";
// Content extraction disabled; no pdf.js import needed
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import DashboardBreadcrumbs from "@/components/ui/DashboardBreadcrumbs";
import { useApiClient } from "@/hooks/use-api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { supabase } from "@/lib/supabase";
import { UsageTracker } from "@/lib/services/usage-tracker";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";

export interface Clause {
  type: "success" | "warning" | "danger" | "info" | string;
  name: string;
  comment?: string;
  references?: string[];
}

export interface AnalysisResult {
  // Legacy format support
  summary?: string;
  risk_scores?: {
    overall: "Bajo" | "Medio" | "Alto" | string;
    legal: "Bajo" | "Medio" | "Alto" | string;
    compliance: "Bajo" | "Medio" | "Alto" | string;
    data_protection: "Bajo" | "Medio" | "Alto" | string;
  };
  clauses?: Clause[];
  recommendations?: string[];

  // Enhanced format
  risk_level?: "bajo" | "medio" | "alto" | string;
  risk_score?: number;
  critical_clauses?: string[];
  obligations?: Array<{
    parte: string;
    obligaciones: string[];
  }>;
  risks?: string[];
  impact?: "bajo" | "medio" | "alto" | string;
  compliance_status?: "cumple" | "parcial" | "no_cumple" | string;
  compliance_score?: number;
  legal_terms?: Record<string, string>;
  jurisprudence_references?: (
    | string
    | {
      referencia?: string;
      descripcion?: string;
      aplicacion?: string;
    }
  )[];
  memory_insights?:
  | string
  | {
    patrones_previos?: string;
    preferencias_usuario?: string;
    historial_cumplimiento?: string;
  };
  knowledge_sources?: (
    | string
    | {
      fuente?: string;
      tipo?: string;
      relevancia?: string;
    }
  )[];

  // PDF Report
  summary_pdf_url?: string;
}

export interface AnalysisHistoryItem {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadDate: Date;
  analysisResult: AnalysisResult;
  fileUrl?: string;
  riskLevel?: string;
  riskScore?: number;
  complianceStatus?: string;
  complianceScore?: number;
}

const client = createLegalAIAssistantClient(API_BASE_URL);

interface FileContent {
  text: string;
  metadata: {
    name: string;
    size: string;
    type: string;
    lastModified: string;
  };
}

// Helper for clause icon and color
const clauseTypeIcon = {
  success: <CheckCircle className="text-green-500 inline mr-2" size={20} />,
  warning: <AlertTriangle className="text-yellow-500 inline mr-2" size={20} />,
  danger: <XCircle className="text-red-500 inline mr-2" size={20} />,
  info: <Info className="text-blue-500 inline mr-2" size={20} />,
};
const clauseTypeText = {
  success: "text-green-700",
  warning: "text-yellow-700",
  danger: "text-red-700",
  info: "text-blue-700",
};
const riskColor = {
  Bajo: "bg-green-500",
  Medio: "bg-yellow-500",
  Alto: "bg-red-500",
};
const riskText = {
  Bajo: "text-green-700",
  Medio: "text-yellow-700",
  Alto: "text-red-700",
};
const riskPercent = {
  Bajo: 33,
  Medio: 66,
  Alto: 100,
};

// Enhanced format helpers
const getRiskLevel = (result: AnalysisResult | null): string => {
  if (!result) return "Medio";
  if (result.risk_level) {
    return (
      result.risk_level.charAt(0).toUpperCase() + result.risk_level.slice(1)
    );
  }
  if (result.risk_scores?.overall) {
    return result.risk_scores.overall;
  }
  return "Medio";
};

const getRiskScore = (result: AnalysisResult | null): number => {
  if (!result) return 66;
  if (result.risk_score !== undefined) {
    return result.risk_score;
  }
  const level = getRiskLevel(result);
  return riskPercent[level as keyof typeof riskPercent] || 66;
};

const getComplianceStatus = (result: AnalysisResult | null): string => {
  if (!result) return "Parcial";
  if (result.compliance_status) {
    const statusMap = {
      cumple: "Cumple",
      parcial: "Parcial",
      no_cumple: "No cumple",
    };
    return (
      statusMap[result.compliance_status as keyof typeof statusMap] ||
      result.compliance_status
    );
  }
  return "Parcial";
};

const getComplianceScore = (result: AnalysisResult | null): number => {
  if (!result) return 65;
  if (result.compliance_score !== undefined) {
    return result.compliance_score;
  }
  return 65; // Default
};

// Helper function to parse risk objects
const parseRiskObject = (riskString: string) => {
  try {
    // Try to parse as JSON object
    const parsed = JSON.parse(riskString);
    return {
      clausula: parsed.clausula || "",
      riesgo: parsed.riesgo || "",
      severidad: parsed.severidad || "",
      impacto: parsed.impacto || "",
      recomendacion: parsed.recomendacion || "",
    };
  } catch {
    // If parsing fails, return as simple string
    return {
      clausula: "",
      riesgo: "",
      severidad: "",
      impacto: "",
      recomendacion: riskString,
    };
  }
};

export default function ContractAnalysisDashboard() {
  const { toast } = useToast();
  const apiClient = useApiClient();
  const { user } = useAuth();
  const { startReviewRun, getRun } = useAgentExecution();
  const { openDocument } = useDocumentViewer();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<FileContent | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [analysisOptions, setAnalysisOptions] = useState({
    riskFinancial: true,
    riskLegal: true,
    riskCompliance: true,
    clauseImportant: true,
    clauseObligations: true,
    clauseTermination: true,
    complianceRegulatory: true,
    complianceInternal: true,
    complianceIndustry: true,
  });

  const reviewRunId = "contract-review-run";
  const currentRun = getRun(reviewRunId);

  const [localResult, setLocalResult] = useState<AnalysisResult | null>(null);
  const analysisResult = (currentRun?.metadata as AnalysisResult) || localResult;

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const isLoading = currentRun?.status === "loading" || isAnalyzing;
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "analyze" | "progress" | "results" | "history"
  >("analyze");
  const [progress, setProgress] = useState(0); // 0-100
  const [stepStatus, setStepStatus] = useState<{
    clauses: "pending" | "done";
    risks: "pending" | "done";
    recommendations: "pending" | "done";
  }>({ clauses: "pending", risks: "pending", recommendations: "pending" });
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisHistoryItem[]>(
    []
  );

  // Despachos Integration State
  const [showProcesoDialog, setShowProcesoDialog] = useState(false);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [procesos, setProcesos] = useState<ScrapperProceso[]>([]);
  const [procesoFiles, setProcesoFiles] = useState<ProcesoArchivo[]>([]);
  const [selectedProceso, setSelectedProceso] = useState<ScrapperProceso | null>(null);
  const [selectedDespachoFile, setSelectedDespachoFile] = useState<ProcesoArchivo | null>(null);
  const [loadingProcesos, setLoadingProcesos] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [isDespachoAnalysis, setIsDespachoAnalysis] = useState(false);

  // Load analysis history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem("contract-analysis-history");
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory).map(
          (item: AnalysisHistoryItem & { uploadDate: string }) => ({
            ...item,
            uploadDate: new Date(item.uploadDate),
          })
        );
        setAnalysisHistory(parsedHistory);
      } catch (error) {
        console.error("Error loading analysis history:", error);
      }
    }
  }, []);

  // Save analysis history to localStorage whenever it changes
  useEffect(() => {
    if (analysisHistory.length > 0) {
      localStorage.setItem(
        "contract-analysis-history",
        JSON.stringify(analysisHistory)
      );
    }
  }, [analysisHistory]);

  // Upload file using Supabase Storage
  const uploadFileToSupabase = async (file: File): Promise<string> => {
    // Check if user is authenticated
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session) {
      throw new Error("Usuario no autenticado. Por favor, inicie sesión.");
    }

    // Generate unique identifier
    const uniqueId = `${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 15)}`;
    const fileExtension = file.name.split(".").pop();
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const uniqueFileName = `${fileNameWithoutExt}-${uniqueId}.${fileExtension}`;
    const filePath = uniqueFileName;

    // Starting upload to Supabase Storage

    try {
      // Upload file to Supabase Storage with user context
      const { data, error } = await supabase.storage
        .from(SUPABASE_S3_BUCKET)
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Supabase upload error:", error);

        // Handle specific RLS error
        if (error.message.includes("row-level security policy")) {
          throw new Error(
            "Error de permisos: No tiene permisos para subir archivos. Contacte al administrador."
          );
        }

        throw new Error(`Error al subir archivo: ${error.message}`);
      }

      // Supabase upload successful

      // Get public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from(SUPABASE_S3_BUCKET)
        .getPublicUrl(filePath);

      // Public URL generated
      return urlData.publicUrl;
    } catch (error) {
      console.error("❌ Supabase upload error:", error);
      throw new Error(
        `Error al subir archivo a Supabase: ${error instanceof Error ? error.message : "Error desconocido"
        }`
      );
    }
  };

  // Cloudflare R2 Upload Function (COMMENTED OUT - USING SUPABASE NOW)
  /*
  const uploadFileToR2 = async (file: File): Promise<string> => {
    // Generate unique identifier
    const uniqueId = `${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 15)}`;
    const fileExtension = file.name.split(".").pop();
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const uniqueFileName = `${fileNameWithoutExt}-${uniqueId}.${fileExtension}`;


    try {
      const formData = new FormData();
      // Create a new File object with the unique filename
      const fileWithUniqueName = new File([file], uniqueFileName, {
        type: file.type,
      });
      formData.append("file", fileWithUniqueName);


      const response = await fetch("/api/upload-to-r2", {
        // Relative URL to your Pages Function route
        method: "POST",
        body: formData,
      });


      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      return result.url;
    } catch (error) {
      console.error("❌ Upload error:", error);
      throw new Error(
        `Error al subir archivo a la nube: ${
          error instanceof Error ? error.message : "Error desconocido"
        }`
      );
    }
  };
  */

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // File validation is handled by dropzone configuration
        // No need for duplicate validation here

        // Always show spinner when processing any file
        setIsUploading(true);
        setUploadedFile(file);
        setIsDespachoAnalysis(false);
        setError(null);
        setLocalResult(null); // Clear previous content
        setUploadedFileUrl(null); // Clear previous URL

        try {
          // Build lightweight metadata for preview
          setFileContent({
            text: "",
            metadata: {
              name: file.name,
              size: (file.size / 1024).toFixed(2) + " KB",
              type: file.type,
              lastModified: new Date(file.lastModified).toLocaleDateString(),
            },
          });

          // Upload file to Supabase Storage
          // Uploading file to Supabase Storage
          try {
            const fileUrl = await uploadFileToSupabase(file);
            setUploadedFileUrl(fileUrl);
            // File uploaded to Supabase
            // Generate a simple thumbnail placeholder based on type
            const placeholder =
              file.type === "application/pdf"
                ? "/assets/logo.svg"
                : file.type.includes("word")
                  ? "/assets/logo.svg"
                  : "/assets/logo.svg";
            setThumbnailUrl(placeholder);
          } catch (uploadError) {
            console.error("Supabase upload failed:", uploadError);
            setUploadedFileUrl(null);
            toast({
              title: "Error al subir",
              description: "No se pudo subir el archivo a Supabase Storage.",
              variant: "destructive",
            });
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Error al procesar el archivo.";
          console.error("File processing error:", error);
          setError(errorMessage);
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
          setFileContent(null);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: 2 * 1024 * 1024, // 2MB in bytes
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections[0];
      const error = rejection.errors[0];

      if (error?.code === "file-too-large") {
        const fileSizeMB = (rejection.file.size / (1024 * 1024)).toFixed(2);
        toast({
          title: "Archivo demasiado grande",
          description: `El archivo (${fileSizeMB}MB) excede el límite de 2MB.`,
          variant: "destructive",
        });
      } else if (error?.code === "file-invalid-type") {
        const fileName = rejection.file.name;
        const fileExtension =
          fileName.split(".").pop()?.toUpperCase() || "desconocido";
        toast({
          title: "Tipo de archivo no válido",
          description: `El archivo .${fileExtension} no es compatible. Solo se permiten PDF, DOC y DOCX.`,
          variant: "destructive",
        });
      } else if (error?.code === "too-many-files") {
        toast({
          title: "Demasiados archivos",
          description: "Solo puedes subir un archivo a la vez.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error al subir archivo",
          description: "No se pudo procesar el archivo seleccionado.",
          variant: "destructive",
        });
      }
    },
  });

  const handleCheckboxChange = (id: keyof typeof analysisOptions) => {
    setAnalysisOptions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenProcesoDialog = async () => {
    setShowProcesoDialog(true);
    setLoadingProcesos(true);
    try {
      // Query direct from procesos to show Active processes with Titles/Clients
      const { data, error } = await supabase
        .from('procesos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map to the internal ScrapperProceso interface for compatibility
      const mappedProcesos = (data || []).map(p => ({
        numero_proceso: p.numero_proceso,
        radicado: p.numero_proceso,
        asunto: p.asunto || p.clase || 'Proceso sin título',
        metadata: { client_name: p.metadata?.client_name || '' }
      }));

      setProcesos(mappedProcesos);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los expedientes activos.",
        variant: "destructive",
      });
    } finally {
      setLoadingProcesos(false);
    }
  };

  const handleSelectProceso = async (p: ScrapperProceso) => {
    setSelectedProceso(p);
    setShowProcesoDialog(false);
    setShowFileDialog(true);
    setLoadingFiles(true);
    try {
      const files = await getProcesoFiles(p.numero_proceso || p.radicado || "");
      setProcesoFiles(files);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los archivos del expediente.",
        variant: "destructive",
      });
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleSelectDespachoFile = (file: ProcesoArchivo) => {
    setSelectedDespachoFile(file);
    setShowFileDialog(false);
    setIsDespachoAnalysis(true);
    setUploadedFile(new File([], file.file_name)); // Dummy file for UI
    setUploadedFileUrl(file.file_path);

    // Set file content for preview
    setFileContent({
      text: "Documento vinculado desde Despachos. El análisis se realizará directamente desde el servidor.",
      metadata: {
        name: file.file_name,
        size: (file.file_size / 1024).toFixed(2) + " KB",
        type: file.content_type || "application/pdf",
        lastModified: new Date(file.created_at).toLocaleDateString(),
      }
    });
  };

  const startDespachoAnalysis = async () => {
    if (!selectedProceso || !selectedDespachoFile) return;

    setIsAnalyzing(true);
    setProgress(10);
    setError(null);
    setLocalResult(null);
    setActiveTab("progress");

    try {
      // Refresh token
      const { data: { session } } = await supabase.auth.refreshSession();
      if (!session?.access_token) throw new Error("Error de sesión");

      const response = await fetch(`${API_BASE_URL}/api/v1/despachos/analyze-file`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          radicado: selectedProceso.numero_proceso || selectedProceso.radicado,
          file_name: selectedDespachoFile.file_name,
          contract_type: "contrato_prestacion_servicios"
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error en el análisis del expediente");
      }

      const result = await response.json();
      setLocalResult(result.analysis);

      // Update history
      const historyItem: AnalysisHistoryItem = {
        id: crypto.randomUUID(),
        fileName: selectedDespachoFile.file_name,
        fileSize: selectedDespachoFile.file_size,
        fileType: selectedDespachoFile.content_type,
        uploadDate: new Date(),
        analysisResult: result.analysis,
        riskLevel: getRiskLevel(result.analysis),
        riskScore: getRiskScore(result.analysis),
      };
      setAnalysisHistory(prev => [historyItem, ...prev]);

      setTimeout(() => setActiveTab("results"), 800);
      toast({
        title: "Análisis Completado",
        description: "El expediente ha sido analizado con éxito.",
      });
    } catch (err: any) {
      setError(err.message);
      setActiveTab("analyze");
      toast({
        title: "Error de Análisis",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyze = async () => {
    if (!uploadedFile || !uploadedFileUrl) {
      toast({
        title: "Falta subir el archivo",
        description: "Sube el archivo primero para obtener su URL.",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setLocalResult(null);
    setActiveTab("progress");
    setProgress(10);
    setStepStatus({
      clauses: "pending",
      risks: "pending",
      recommendations: "pending",
    });

    try {
      // Refresh token before making request to ensure it's valid
      const {
        data: { session },
      } = await supabase.auth.refreshSession();

      if (!session?.access_token) {
        throw new Error(
          "Error de autenticación. Por favor, inicie sesión nuevamente."
        );
      }

      const requestData = {
        file_url: uploadedFileUrl,
        language: DEFAULT_LANGUAGE,
        jurisdiction: DEFAULT_JURISDICTION,
        analysis_options: {
          riskFinancial: analysisOptions.riskFinancial,
          riskLegal: analysisOptions.riskLegal,
          riskCompliance: analysisOptions.riskCompliance,
          clauseImportant: analysisOptions.clauseImportant,
          clauseObligations: analysisOptions.clauseObligations,
          clauseTermination: analysisOptions.clauseTermination,
          complianceRegulatory: analysisOptions.complianceRegulatory,
          complianceInternal: analysisOptions.complianceInternal,
          complianceIndustry: analysisOptions.complianceIndustry,
        },
        metadata: {
          fileName: uploadedFile.name,
          fileSize: uploadedFile.size,
          fileType: uploadedFile.type,
          extractedText: "",
          extractedTextLength: 0,
          uploadedAt: new Date().toISOString(),
          supabaseUrl: uploadedFileUrl,
          bucketName: SUPABASE_S3_BUCKET,
        },
      };

      await startReviewRun(reviewRunId, requestData, apiClient, user?.id || null);

      // The results will be picked up by the context subscription
      setTimeout(() => setActiveTab("results"), 800);
    } catch (err) {
      let errorMessage = "Ocurrió un error durante el análisis.";

      // Log detailed error information for debugging
      console.error("Contract analysis error:", err);

      // Try to extract error details from the response
      if (err && typeof err === "object" && "error" in err) {
        const errorObj = err as { error: unknown };
        console.error("Error object details:", errorObj.error);

        if (
          errorObj.error &&
          typeof errorObj.error === "object" &&
          errorObj.error !== null
        ) {
          // Log the full error structure
          console.error(
            "Full error structure:",
            JSON.stringify(errorObj.error, null, 2)
          );

          if ("detail" in errorObj.error) {
            const details = (errorObj.error as { detail: unknown }).detail;
            console.error("Error details:", details);

            if (Array.isArray(details) && details.length > 0) {
              errorMessage = `Error de validación: ${details
                .map(
                  (d: { msg?: string; type?: string; loc?: string[] }) =>
                    `${d.type || "error"} at ${d.loc?.join(".") || "unknown"
                    }: ${d.msg || "Error desconocido"}`
                )
                .join(", ")}`;
            } else if (typeof details === "string") {
              // Check for specific backend error messages
              if (
                details.includes(
                  "Could not extract text from the downloaded file"
                )
              ) {
                errorMessage =
                  "No se pudo procesar el archivo. El archivo podría estar corrupto o protegido con contraseña. Por favor, verifique que el archivo no esté dañado y vuelva a intentarlo.";
              } else if (details.includes("InvalidKey")) {
                errorMessage =
                  "Error de acceso al archivo. El archivo no se pudo encontrar en el almacenamiento. Por favor, intente subir el archivo nuevamente.";
              } else if (
                details.includes("Either file_path or text must be provided")
              ) {
                errorMessage =
                  "Error de configuración del análisis. Por favor, intente nuevamente o contacte al soporte técnico.";
              } else if (
                details.includes("'NoneType' object has no attribute 'upper'")
              ) {
                errorMessage =
                  "Error interno del servidor. Por favor, intente nuevamente en unos momentos.";
              } else {
                errorMessage = details;
              }
            }
          }
        } else if (errorObj.error && typeof errorObj.error === "string") {
          // Check for specific backend error messages
          if (
            errorObj.error.includes(
              "Could not extract text from the downloaded file"
            )
          ) {
            errorMessage =
              "No se pudo procesar el archivo. El archivo podría estar corrupto o protegido con contraseña. Por favor, verifique que el archivo no esté dañado y vuelva a intentarlo.";
          } else if (errorObj.error.includes("InvalidKey")) {
            errorMessage =
              "Error de acceso al archivo. El archivo no se pudo encontrar en el almacenamiento. Por favor, intente subir el archivo nuevamente.";
          } else if (
            errorObj.error.includes("Either file_path or text must be provided")
          ) {
            errorMessage =
              "Error de configuración del análisis. Por favor, intente nuevamente o contacte al soporte técnico.";
          } else if (
            errorObj.error.includes(
              "'NoneType' object has no attribute 'upper'"
            )
          ) {
            errorMessage =
              "Error interno del servidor. Por favor, intente nuevamente en unos momentos.";
          } else {
            errorMessage = errorObj.error;
          }
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;

        // Check for specific error types
        if (err.message.includes("401")) {
          errorMessage =
            "Error de autenticación. Por favor, inicie sesión nuevamente.";
          console.error("Authentication error detected");
        } else if (err.message.includes("403")) {
          errorMessage = "No tiene permisos para realizar esta acción.";
          console.error("Authorization error detected");
        } else if (err.message.includes("400")) {
          errorMessage =
            "Error en el formato de la solicitud. Verifique que el archivo sea válido.";
          console.error("Bad request error detected");
        } else if (err.message.includes("413")) {
          errorMessage =
            "El archivo es demasiado grande. El tamaño máximo es 10MB.";
          console.error("File too large error detected");
        } else if (err.message.includes("415")) {
          errorMessage = "Tipo de archivo no soportado. Use PDF, DOC o DOCX.";
          console.error("Unsupported file type error detected");
        } else if (
          err.message.includes(
            "Could not extract text from the downloaded file"
          )
        ) {
          errorMessage =
            "No se pudo procesar el archivo. El archivo podría estar corrupto o protegido con contraseña. Por favor, verifique que el archivo no esté dañado y vuelva a intentarlo.";
          console.error("File text extraction error detected");
        } else if (err.message.includes("InvalidKey")) {
          errorMessage =
            "Error de acceso al archivo. El archivo no se pudo encontrar en el almacenamiento. Por favor, intente subir el archivo nuevamente.";
          console.error("Invalid key error detected");
        } else if (
          err.message.includes("Either file_path or text must be provided")
        ) {
          errorMessage =
            "Error de configuración del análisis. Por favor, intente nuevamente o contacte al soporte técnico.";
          console.error("Missing file_path or text error detected");
        } else if (
          err.message.includes("'NoneType' object has no attribute 'upper'")
        ) {
          errorMessage =
            "Error interno del servidor. Por favor, intente nuevamente en unos momentos.";
          console.error("Backend NoneType error detected");
        }
      }

      setError(errorMessage);
      setActiveTab("analyze");
      toast({
        title: "Error de Análisis",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    handleAnalyze(); // Re-run the analysis
  };

  const getRiskColor = (
    level: AnalysisResult["risk_scores"]["overall"]
  ): string => {
    switch (level) {
      case "alto":
        return "#dc3545"; // red
      case "medio":
        return "#ffc107"; // yellow
      case "bajo":
        return "#198754"; // green
      default:
        return "#adb5bd"; // gray
    }
  };

  const getRiskProgress = (
    level: AnalysisResult["risk_scores"]["overall"]
  ): number => {
    switch (level) {
      case "alto":
        return 100;
      case "medio":
        return 66;
      case "bajo":
        return 33;
      default:
        return 0;
    }
  };

  // Render the contract review specific UI
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Revisión de Contratos con IA
        </h1>
        <p className="text-muted-foreground mt-1">
          Revisión automática de contratos y evaluación de riesgos
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as typeof activeTab)}
        className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="analyze">
            <UploadCloud className="inline mr-2" size={18} /> Subir contrato
          </TabsTrigger>
          <TabsTrigger value="progress" disabled={!isLoading}>
            <Search className="inline mr-2" size={18} /> Analizando
          </TabsTrigger>
          <TabsTrigger value="results" disabled={!analysisResult}>
            <FileText className="inline mr-2" size={18} /> Resultados
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="inline mr-2" size={18} /> Historial
          </TabsTrigger>
        </TabsList>

        {/* Analizar contrato Tab */}
        <TabsContent value="analyze">
          {/* 1. Upload Section */}
          <Card
            {...getRootProps()}
            className={`border-2 border-dashed ${isDragActive ? "border-primary bg-primary/10" : "border-muted"
              } hover:border-primary/80 transition-colors relative ${isUploading ? "cursor-not-allowed opacity-75" : "cursor-pointer"
              }`}>
            <CardContent className="p-6 text-center">
              <input {...getInputProps()} disabled={isUploading} />
              {isUploading ? (
                <>
                  <Loader2 className="mx-auto h-12 w-12 text-primary mb-4 animate-spin" />
                  <p className="text-primary font-semibold">
                    Procesando archivo...
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Subiendo a Supabase Storage
                  </p>
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full animate-pulse"
                        style={{ width: "60%" }}></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <UploadCloud className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  {isDragActive ? (
                    <p className="text-primary font-semibold">
                      Suelta el archivo aquí...
                    </p>
                  ) : (
                    <>
                      <p className="font-semibold mb-1">
                        Arrastra y suelta tu contrato aquí, o haz clic para
                        seleccionar
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Archivos soportados: PDF, DOC, DOCX (máx. 2MB)
                      </p>
                    </>
                  )}
                </>
              )}
              {uploadedFile && !isUploading && (
                <p className="mt-4 text-sm font-medium text-green-600">
                  {uploadedFileUrl ? (
                    <>✓ Archivo listo para análisis: {uploadedFile.name}</>
                  ) : (
                    <>Archivo seleccionado: {uploadedFile.name}</>
                  )}
                </p>
              )}
            </CardContent>
          </Card>

          {/* 2. Analysis Options */}
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {/* Risk Assessment Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <ShieldAlert className="mr-2 h-5 w-5 text-red-500" />{" "}
                  Evaluación Riesgos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries({
                  riskFinancial: "Riesgos Financieros",
                  riskLegal: "Riesgos Legales",
                  riskCompliance: "Riesgos Cumplimiento",
                }).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={
                        analysisOptions[key as keyof typeof analysisOptions]
                      }
                      onCheckedChange={() =>
                        handleCheckboxChange(
                          key as keyof typeof analysisOptions
                        )
                      }
                    />
                    <Label
                      htmlFor={key}
                      className="text-sm font-normal cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </CardContent>
            </Card>
            {/* Clause Extraction Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-blue-500" /> Extracción
                  Cláusulas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries({
                  clauseImportant: "Cláusulas Importantes",
                  clauseObligations: "Obligaciones",
                  clauseTermination: "Términos Finalización",
                }).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={
                        analysisOptions[key as keyof typeof analysisOptions]
                      }
                      onCheckedChange={() =>
                        handleCheckboxChange(
                          key as keyof typeof analysisOptions
                        )
                      }
                    />
                    <Label
                      htmlFor={key}
                      className="text-sm font-normal cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </CardContent>
            </Card>
            {/* Compliance Check Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500" />{" "}
                  Verificación Cumplimiento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries({
                  complianceRegulatory: "Normativa Regulatoria",
                  complianceInternal: "Políticas Internas",
                  complianceIndustry: "Estándares Industria",
                }).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={
                        analysisOptions[key as keyof typeof analysisOptions]
                      }
                      onCheckedChange={() =>
                        handleCheckboxChange(
                          key as keyof typeof analysisOptions
                        )
                      }
                    />
                    <Label
                      htmlFor={key}
                      className="text-sm font-normal cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          {/* Iniciar Análisis Button */}
          <div className="flex justify-end mt-6">
            <Button
              className="w-full md:w-auto"
              onClick={handleAnalyze}
              disabled={!uploadedFileUrl || isLoading || isUploading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                  Analizando...
                </>
              ) : isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Procesando
                  archivo...
                </>
              ) : (
                "Iniciar Análisis"
              )}
            </Button>
          </div>

          {/* Vista Previa del Documento (now below in the same tab) */}
          <Card className="md:col-span-2 mt-8">
            <CardHeader>
              <CardTitle>Vista Previa del Documento</CardTitle>
            </CardHeader>
            <CardContent style={{ minHeight: "300px" }} className="relative">
              {(isLoading || isUploading) && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-3">
                    {isUploading ? "Subiendo a Supabase..." : "Analizando..."}
                  </span>
                </div>
              )}
              {!fileContent ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <FileText className="h-16 w-16 mb-4" />
                  <p>Sube un contrato para ver la vista previa.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm border p-3 rounded-md bg-muted/50">
                    {uploadedFileUrl && (
                      <div className="col-span-2 flex items-center gap-3">
                        <div className="w-14 h-14 rounded border bg-white flex items-center justify-center overflow-hidden">
                          {/* Simple thumbnail placeholder */}
                          <img
                            src={thumbnailUrl || "/assets/placeholder.svg"}
                            alt="Vista previa"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        <div className="text-xs">
                          <div className="font-semibold">Archivo subido</div>
                          <div className="text-green-600 font-medium">
                            ✓ Archivo procesado correctamente
                          </div>
                        </div>
                      </div>
                    )}
                    <p>
                      <span className="font-semibold">Nombre:</span>{" "}
                      {fileContent.metadata.name}
                    </p>
                    <p>
                      <span className="font-semibold">Tamaño:</span>{" "}
                      {fileContent.metadata.size}
                    </p>
                    <p>
                      <span className="font-semibold">Tipo:</span>{" "}
                      {fileContent.metadata.type}
                    </p>
                    <p>
                      <span className="font-semibold">Modificado:</span>{" "}
                      {fileContent.metadata.lastModified}
                    </p>
                    {uploadedFileUrl && (
                      <p className="col-span-2">
                        <span className="font-semibold text-green-600">
                          ✓ Subido a Supabase Storage correctamente
                        </span>
                      </p>
                    )}
                    {!uploadedFileUrl && uploadedFile && !isUploading && (
                      <p className="col-span-2">
                        <span className="font-semibold text-yellow-600">
                          ⚠ Archivo local:
                        </span>{" "}
                        <span className="text-xs text-muted-foreground">
                          El archivo será enviado directamente al servidor
                        </span>
                      </p>
                    )}
                  </div>
                  {/* No content extraction preview shown */}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analizando Tab */}
        <TabsContent value="progress">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Search size={64} className="text-blue-300 mb-4 animate-spin" />
            <h2 className="text-2xl font-bold mb-2">Analizando contrato</h2>
            <p className="text-muted-foreground mb-6 text-center max-w-xl">
              Nuestro sistema está revisando su documento, identificando
              cláusulas y evaluando posibles riesgos...
            </p>
            <Progress value={progress} className="h-2 w-full max-w-lg mb-2" />
            <div className="flex justify-between w-full max-w-lg text-xs text-muted-foreground mb-6">
              <span>Análisis en proceso</span>
              <span>{progress}%</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-2xl mt-4">
              <div className="rounded-xl border p-4 text-center bg-background">
                <div className="text-sm text-muted-foreground mb-1">
                  Identificando cláusulas
                </div>
                <div className="font-bold text-lg">
                  {stepStatus.clauses === "done"
                    ? "Completado"
                    : "En progreso..."}
                </div>
              </div>
              <div className="rounded-xl border p-4 text-center bg-background">
                <div className="text-sm text-muted-foreground mb-1">
                  Evaluando riesgos
                </div>
                <div className="font-bold text-lg">
                  {stepStatus.risks === "done"
                    ? "Completado"
                    : "En progreso..."}
                </div>
              </div>
              <div className="rounded-xl border p-4 text-center bg-background">
                <div className="text-sm text-muted-foreground mb-1">
                  Generando recomendaciones
                </div>
                <div className="font-bold text-lg">
                  {stepStatus.recommendations === "done"
                    ? "Completado"
                    : "En progreso..."}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Resultados Tab */}
        <TabsContent value="results">
          <div className="space-y-8">
            {/* Enhanced Analysis Summary */}
            <Card>
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                      <Info className="text-yellow-500" /> Resumen del análisis
                    </h2>

                    {/* Enhanced or Legacy Summary */}
                    {analysisResult?.memory_insights ? (
                      <div className="mb-4">
                        <p className="text-sm text-muted-foreground mb-2">
                          Análisis contextual:
                        </p>
                        {typeof analysisResult.memory_insights === "string" ? (
                          <p className="italic">
                            {analysisResult.memory_insights}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {analysisResult.memory_insights
                              .patrones_previos && (
                                <div>
                                  <span className="font-medium text-sm">
                                    Patrones previos:
                                  </span>
                                  <p className="text-sm italic">
                                    {
                                      analysisResult.memory_insights
                                        .patrones_previos
                                    }
                                  </p>
                                </div>
                              )}
                            {analysisResult.memory_insights
                              .preferencias_usuario && (
                                <div>
                                  <span className="font-medium text-sm">
                                    Preferencias del usuario:
                                  </span>
                                  <p className="text-sm italic">
                                    {
                                      analysisResult.memory_insights
                                        .preferencias_usuario
                                    }
                                  </p>
                                </div>
                              )}
                            {analysisResult.memory_insights
                              .historial_cumplimiento && (
                                <div>
                                  <span className="font-medium text-sm">
                                    Historial de cumplimiento:
                                  </span>
                                  <p className="text-sm italic">
                                    {
                                      analysisResult.memory_insights
                                        .historial_cumplimiento
                                    }
                                  </p>
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="mb-4">{analysisResult?.summary}</p>
                    )}

                    <div className="mb-2">
                      <span className="font-medium">
                        Puntuación de riesgo:{" "}
                      </span>
                      <span
                        className={`font-semibold ${riskText[
                          getRiskLevel(
                            analysisResult
                          ) as keyof typeof riskText
                        ] || "text-yellow-700"
                          }`}>
                        {getRiskLevel(analysisResult)}
                      </span>
                      {analysisResult?.risk_score && (
                        <span className="ml-2 text-sm text-muted-foreground">
                          ({analysisResult.risk_score}/100)
                        </span>
                      )}
                    </div>
                    <Progress
                      value={getRiskScore(analysisResult)}
                      className={`h-2 ${riskColor[
                        getRiskLevel(analysisResult) as keyof typeof riskColor
                      ] || "bg-yellow-500"
                        }`}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Riesgo bajo</span>
                      <span>Riesgo alto</span>
                    </div>
                  </div>

                  {/* Enhanced Risk Metrics */}
                  <div className="flex flex-row md:flex-col gap-4 min-w-[220px] justify-center">
                    <div className="text-center border rounded-lg px-6 py-2">
                      <div className="text-xs text-muted-foreground">
                        Estado de cumplimiento
                      </div>
                      <div className="font-semibold text-lg text-blue-600">
                        {getComplianceStatus(analysisResult)}
                      </div>
                      {analysisResult?.compliance_score && (
                        <div className="text-xs text-muted-foreground">
                          {analysisResult.compliance_score}%
                        </div>
                      )}
                    </div>
                    <div className="text-center border rounded-lg px-6 py-2">
                      <div className="text-xs text-muted-foreground">
                        Impacto potencial
                      </div>
                      <div
                        className={`font-semibold text-lg ${analysisResult?.impact === "alto"
                          ? "text-red-600"
                          : analysisResult?.impact === "medio"
                            ? "text-yellow-600"
                            : "text-green-600"
                          }`}>
                        {analysisResult?.impact
                          ? analysisResult.impact.charAt(0).toUpperCase() +
                          analysisResult.impact.slice(1)
                          : "Medio"}
                      </div>
                    </div>
                    <div className="text-center border rounded-lg px-6 py-2">
                      <div className="text-xs text-muted-foreground">
                        Cláusulas críticas
                      </div>
                      <div className="font-semibold text-lg text-orange-600">
                        {analysisResult?.critical_clauses?.length || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Critical Clauses */}
            {analysisResult?.critical_clauses &&
              Array.isArray(analysisResult.critical_clauses) &&
              analysisResult.critical_clauses.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="text-red-500" size={20} />
                      Cláusulas Críticas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {analysisResult.critical_clauses.map((clause, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <XCircle
                            className="text-red-500 mt-0.5 flex-shrink-0"
                            size={16}
                          />
                          <span className="text-sm">{clause}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

            {/* Obligations */}
            {analysisResult?.obligations &&
              Array.isArray(analysisResult.obligations) &&
              analysisResult.obligations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="text-green-500" size={20} />
                      Obligaciones Identificadas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analysisResult.obligations.map(
                        (obligationGroup, idx) => (
                          <div
                            key={idx}
                            className="border border-green-200 rounded-lg p-4 bg-green-50">
                            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                              <CheckCircle
                                className="text-green-500"
                                size={16}
                              />
                              {obligationGroup?.parte ||
                                "Parte no especificada"}
                            </h4>
                            <ul className="space-y-2">
                              {obligationGroup.obligaciones &&
                                Array.isArray(obligationGroup.obligaciones) ? (
                                obligationGroup.obligaciones.map(
                                  (obligation, obligationIdx) => (
                                    <li
                                      key={obligationIdx}
                                      className="flex items-start gap-3">
                                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                      <span className="text-sm">
                                        {obligation}
                                      </span>
                                    </li>
                                  )
                                )
                              ) : (
                                <li className="text-sm text-muted-foreground">
                                  No se encontraron obligaciones específicas
                                </li>
                              )}
                            </ul>
                          </div>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Legal Risks */}
            {analysisResult?.risks &&
              Array.isArray(analysisResult.risks) &&
              analysisResult.risks.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldAlert className="text-orange-500" size={20} />
                      Riesgos Legales Identificados
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analysisResult.risks.map((risk, idx) => {
                        const parsedRisk = parseRiskObject(risk);
                        return (
                          <div
                            key={idx}
                            className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-start gap-3 mb-3">
                              <AlertTriangle
                                className="text-orange-500 mt-0.5 flex-shrink-0"
                                size={16}
                              />
                              <div className="flex-1">
                                {parsedRisk.clausula && (
                                  <div className="mb-2">
                                    <span className="font-semibold text-orange-800">
                                      Cláusula:
                                    </span>
                                    <span className="ml-2 text-sm">
                                      {parsedRisk.clausula}
                                    </span>
                                  </div>
                                )}
                                {parsedRisk.riesgo && (
                                  <div className="mb-2">
                                    <span className="font-semibold text-orange-800">
                                      Riesgo:
                                    </span>
                                    <span className="ml-2 text-sm">
                                      {parsedRisk.riesgo}
                                    </span>
                                  </div>
                                )}
                                {parsedRisk.severidad && (
                                  <div className="mb-2">
                                    <span className="font-semibold text-orange-800">
                                      Severidad:
                                    </span>
                                    <span
                                      className={`ml-2 text-sm font-medium ${parsedRisk.severidad.includes("alto")
                                        ? "text-red-600"
                                        : parsedRisk.severidad.includes(
                                          "medio"
                                        )
                                          ? "text-yellow-600"
                                          : "text-green-600"
                                        }`}>
                                      {parsedRisk.severidad}
                                    </span>
                                  </div>
                                )}
                                {parsedRisk.impacto && (
                                  <div className="mb-2">
                                    <span className="font-semibold text-orange-800">
                                      Impacto:
                                    </span>
                                    <span className="ml-2 text-sm">
                                      {parsedRisk.impacto}
                                    </span>
                                  </div>
                                )}
                                {parsedRisk.recomendacion && (
                                  <div>
                                    <span className="font-semibold text-orange-800">
                                      Recomendación:
                                    </span>
                                    <span className="ml-2 text-sm">
                                      {parsedRisk.recomendacion}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Legal Terms */}
            {analysisResult?.legal_terms &&
              Object.keys(analysisResult.legal_terms).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="text-blue-500" size={20} />
                      Términos Legales Relevantes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(analysisResult.legal_terms).map(
                        ([term, definition], idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-1">
                              {term}
                            </h4>
                            <p className="text-sm text-blue-700">
                              {definition}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Jurisprudence References */}
            {analysisResult?.jurisprudence_references &&
              Array.isArray(analysisResult.jurisprudence_references) &&
              analysisResult.jurisprudence_references.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Search className="text-purple-500" size={20} />
                      Referencias Jurisprudenciales
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {analysisResult.jurisprudence_references.map(
                        (reference, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <Search
                              className="text-purple-500 mt-0.5 flex-shrink-0"
                              size={14}
                            />
                            <div className="flex-1">
                              {typeof reference === "string" ? (
                                <span className="text-sm">{reference}</span>
                              ) : (
                                <div className="space-y-1">
                                  {reference.referencia && (
                                    <div>
                                      <span className="font-medium text-sm text-purple-800">
                                        Referencia:
                                      </span>
                                      <span className="ml-2 text-sm">
                                        {reference.referencia}
                                      </span>
                                    </div>
                                  )}
                                  {reference.descripcion && (
                                    <div>
                                      <span className="font-medium text-sm text-purple-800">
                                        Descripción:
                                      </span>
                                      <span className="ml-2 text-sm">
                                        {reference.descripcion}
                                      </span>
                                    </div>
                                  )}
                                  {reference.aplicacion && (
                                    <div>
                                      <span className="font-medium text-sm text-purple-800">
                                        Aplicación:
                                      </span>
                                      <span className="ml-2 text-sm">
                                        {reference.aplicacion}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </li>
                        )
                      )}
                    </ul>
                  </CardContent>
                </Card>
              )}

            {/* Knowledge Sources */}
            {analysisResult?.knowledge_sources &&
              Array.isArray(analysisResult.knowledge_sources) &&
              analysisResult.knowledge_sources.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="text-gray-500" size={20} />
                      Fuentes de Conocimiento
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {analysisResult.knowledge_sources.map((source, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                          <Info
                            className="text-gray-500 mt-0.5 flex-shrink-0"
                            size={14}
                          />
                          <div className="flex-1">
                            {typeof source === "string" ? (
                              <span className="text-sm text-muted-foreground">
                                {source}
                              </span>
                            ) : (
                              <div className="space-y-1">
                                {source.fuente && (
                                  <div>
                                    <span className="font-medium text-sm text-gray-800">
                                      Fuente:
                                    </span>
                                    <span className="ml-2 text-sm text-muted-foreground">
                                      {source.fuente}
                                    </span>
                                  </div>
                                )}
                                {source.tipo && (
                                  <div>
                                    <span className="font-medium text-sm text-gray-800">
                                      Tipo:
                                    </span>
                                    <span className="ml-2 text-sm text-muted-foreground">
                                      {source.tipo}
                                    </span>
                                  </div>
                                )}
                                {source.relevancia && (
                                  <div>
                                    <span className="font-medium text-sm text-gray-800">
                                      Relevancia:
                                    </span>
                                    <span className="ml-2 text-sm text-muted-foreground">
                                      {source.relevancia}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

            {/* Legacy Clauses (fallback) */}
            {analysisResult?.clauses &&
              Array.isArray(analysisResult.clauses) &&
              analysisResult.clauses.length > 0 &&
              !analysisResult.critical_clauses && (
                <Card>
                  <CardHeader>
                    <CardTitle>Cláusulas identificadas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul>
                      {analysisResult.clauses.map((clause, idx) => (
                        <li key={idx} className="mb-4">
                          <div
                            className={`font-semibold flex items-center gap-2 ${clauseTypeText[clause.type] || ""
                              }`}>
                            {clauseTypeIcon[clause.type] || clauseTypeIcon.info}
                            {clause.name}
                          </div>
                          {clause.comment && (
                            <div className="text-xs text-muted-foreground ml-7">
                              {clause.comment}
                            </div>
                          )}
                          {Array.isArray(clause.references) &&
                            clause.references.length > 0 && (
                              <div className="text-xs text-blue-600 ml-7 mt-1">
                                {clause.references.join(", ")}
                              </div>
                            )}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

            {/* Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle>Recomendaciones</CardTitle>
              </CardHeader>
              <CardContent>
                {analysisResult?.recommendations &&
                  Array.isArray(analysisResult.recommendations) &&
                  analysisResult.recommendations.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-2">
                    {analysisResult.recommendations.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">
                    No hay recomendaciones disponibles.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col md:flex-row gap-2 justify-end mt-6">
              <Button variant="outline" onClick={() => setActiveTab("analyze")}>
                Nuevo análisis
              </Button>
              <Button
                variant="outline"
                disabled={!analysisResult?.summary_pdf_url}
                onClick={() => {
                  if (analysisResult?.summary_pdf_url) {
                    openDocument(analysisResult.summary_pdf_url, "Informe Técnico");
                  }
                }}>
                <Download className="mr-2" size={16} />
                Descargar informe
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Consultar con abogado
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Historial Tab */}
        <TabsContent value="history">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="text-blue-500" size={20} />
                  Historial de Análisis de Contratos
                </CardTitle>
                <CardDescription>
                  Revisa todos los análisis de contratos realizados
                  anteriormente
                </CardDescription>
              </CardHeader>
              <CardContent>
                {analysisHistory.length > 0 ? (
                  <div className="space-y-4">
                    {analysisHistory.map((item) => (
                      <Card
                        key={item.id}
                        className="border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <FileText className="text-blue-500" size={20} />
                                <h3 className="font-semibold text-lg">
                                  {item.fileName}
                                </h3>
                                <span className="text-sm text-muted-foreground">
                                  ({(item.fileSize / 1024 / 1024).toFixed(2)}{" "}
                                  MB)
                                </span>
                              </div>

                              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                <div className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {item.uploadDate.toLocaleDateString("es-ES")}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock size={14} />
                                  {item.uploadDate.toLocaleTimeString("es-ES")}
                                </div>
                                <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                                  {item.fileType.split("/")[1]?.toUpperCase() ||
                                    "PDF"}
                                </span>
                              </div>

                              <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">
                                    Riesgo:
                                  </span>
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium ${item.riskLevel === "alto"
                                      ? "bg-red-100 text-red-700"
                                      : item.riskLevel === "medio"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-green-100 text-green-700"
                                      }`}>
                                    {item.riskLevel?.charAt(0).toUpperCase() +
                                      item.riskLevel?.slice(1) || "N/A"}
                                  </span>
                                  {item.riskScore && (
                                    <span className="text-xs text-muted-foreground">
                                      ({item.riskScore}/100)
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">
                                    Cumplimiento:
                                  </span>
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium ${item.complianceStatus === "cumple"
                                      ? "bg-green-100 text-green-700"
                                      : item.complianceStatus === "parcial"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-red-100 text-red-700"
                                      }`}>
                                    {item.complianceStatus
                                      ?.charAt(0)
                                      .toUpperCase() +
                                      item.complianceStatus?.slice(1) || "N/A"}
                                  </span>
                                  {item.complianceScore && (
                                    <span className="text-xs text-muted-foreground">
                                      ({item.complianceScore}%)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setLocalResult(item.analysisResult);
                                  setActiveTab("results");
                                }}>
                                <FileText className="mr-1" size={14} />
                                Ver análisis
                              </Button>
                              {item.fileUrl && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    openDocument(item.fileUrl!, item.fileName)
                                  }>
                                  <Download className="mr-1" size={14} />
                                  Descargar
                                </Button>
                              )}
                              {item.analysisResult.summary_pdf_url && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    openDocument(
                                      item.analysisResult.summary_pdf_url!,
                                      `${item.fileName} - Informe Técnico`
                                    )
                                  }>
                                  <FileText className="mr-1" size={14} />
                                  PDF
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="mx-auto text-gray-400 mb-4" size={48} />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No hay análisis previos
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Los análisis de contratos que realices aparecerán aquí
                    </p>
                    <Button onClick={() => setActiveTab("analyze")}>
                      Realizar primer análisis
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Proceso Selection Dialog */}
      <Dialog open={showProcesoDialog} onOpenChange={setShowProcesoDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-blue-600" />
              Traer Expediente Judicial
            </DialogTitle>
            <DialogDescription className="sr-only">
              Selecciona un expediente judicial de tus despachos para analizar un documento.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 flex-1 overflow-y-auto">
            <p className="text-sm text-slate-500 mb-4">
              Selecciona el proceso del cual deseas traer un documento para analizarlo como contrato.
            </p>
            {loadingProcesos ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm text-slate-400">Cargando tus procesos...</span>
              </div>
            ) : procesos.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-xl">
                <FolderOpen className="h-10 w-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400 font-medium">No tienes procesos guardados</p>
              </div>
            ) : (
              <div className="space-y-2">
                {procesos.map((p) => (
                  <div
                    key={p.numero_proceso}
                    onClick={() => handleSelectProceso(p)}
                    className="p-4 rounded-xl border border-slate-200 bg-white transition-all cursor-pointer flex flex-col gap-1 hover:border-blue-400 hover:bg-blue-50/50"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-blue-600 bg-blue-100/50 px-2 py-0.5 rounded uppercase">
                        {p.numero_proceso}
                      </span>
                      {p.metadata?.client_name && (
                        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                          👩🏽‍⚖️ {p.metadata.client_name}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-slate-700 line-clamp-1">
                      {p.asunto || "Sin asunto"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowProcesoDialog(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Selection Dialog */}
      <Dialog open={showFileDialog} onOpenChange={setShowFileDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-blue-600" />
              Seleccionar Documento del Expediente
            </DialogTitle>
            <DialogDescription className="sr-only">
              Selecciona el archivo específico del expediente que deseas analizar como contrato.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 flex-1 overflow-y-auto">
            {loadingFiles ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm text-slate-400">Cargando archivos del proceso...</span>
              </div>
            ) : procesoFiles.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-xl">
                <FolderOpen className="h-10 w-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400 font-medium">Este proceso no tiene archivos adjuntos</p>
              </div>
            ) : (
              <div className="space-y-2">
                {procesoFiles.map((file) => (
                  <div
                    key={file.id}
                    onClick={() => handleSelectDespachoFile(file)}
                    className="p-4 rounded-xl border border-slate-200 bg-white transition-all cursor-pointer flex items-center gap-3 hover:border-blue-400 hover:bg-blue-50/50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-700 truncate">{file.file_name}</p>
                      <p className="text-xs text-slate-500">{(file.file_size / 1024).toFixed(1)} KB • {new Date(file.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => { setShowFileDialog(false); setShowProcesoDialog(true); }}>Atrás</Button>
            <Button variant="ghost" onClick={() => setShowFileDialog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Despacho Analysis if selected */}
      {selectedDespachoFile && isDespachoAnalysis && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <Button
            onClick={startDespachoAnalysis}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-xl px-10 py-6 rounded-full font-bold text-lg border-4 border-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Analizando...</>
            ) : (
              <>Analizar Documento de Despacho</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
